import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AccentButton from '../../components/ui/AccentButton';

const HomeHero = () => {

  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  // Варианты для текстовых элементов
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1] } 
    },
  };

  return (
    <section className="bg-white py-12 md:py-20 lg:py-24 px-10 md:px-16 lg:px-24 overflow-hidden font-['Inter']">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
        
        {/* ЛЕВАЯ ЧАСТЬ С КАСКАДНОЙ АНИМАЦИЕЙ */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex-1 z-10 text-center lg:text-left"
        >
          <motion.div variants={itemVariants} className="space-y-1 mb-8">
            <h1 className="text-[54px] md:text-[72px] font-[900] leading-[0.9] tracking-tighter text-[#F65814] uppercase">
              {t("hero.title_main")}
            </h1>
            <h2 className="text-[54px] md:text-[72px] font-[900] leading-[0.9] tracking-tighter text-[#0A1128] uppercase">
              {t("hero.title_accent")}<br />{t("hero.title_sub")}
            </h2>
          </motion.div>

          <motion.p variants={itemVariants} className="text-slate-500 text-lg md:text-xl max-w-md mb-10 leading-relaxed font-medium mx-auto lg:mx-0">
            {t("hero.description")}
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <AccentButton to="/register">
              {t("hero.btn_trial")}
            </AccentButton>

            <Link 
              to="/demo" 
              className="bg-white text-[#0A1128] border border-slate-200 text-center py-4 px-8 rounded-[20px] font-[900] uppercase tracking-wider text-[13px] hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 active:scale-95"
            >
              {t("hero.btn_demo")}
            </Link>
          </motion.div>

          {/* Social Proof */}
          <motion.div variants={itemVariants} className="mt-10 flex items-center justify-center lg:justify-start gap-3">
            <div className="flex text-[#F65814] text-sm tracking-widest">★★★★★</div>
            <span className="text-[10px] font-black uppercase tracking-[2px] text-slate-400">
              {t("hero.social_proof")}
            </span>
          </motion.div>
        </motion.div>

        {/* ПРАВАЯ ЧАСТЬ С АНИМАЦИЕЙ ИЗОБРАЖЕНИЯ */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="flex-1 relative w-full max-w-[540px]"
        >
          <div className="relative z-0 group">
            {/* Декоративный фоновый элемент */}
            <motion.div 
              animate={{ rotate: [3, -3, 3] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 bg-[#F65814]/5 rounded-[48px] rotate-3 scale-105 -z-10"
            ></motion.div>
            
            <img 
              src="https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?q=80&w=800&auto=format&fit=crop" 
              alt="Pet Care" 
              className="w-full h-[400px] md:h-[500px] object-cover rounded-[48px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.15)] border-4 border-white"
            />
          </div>

          {/* Парящая плашка Efficiency */}
          <motion.div 
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: [0, -15, 0], opacity: 1 }}
            transition={{ 
              y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
              opacity: { delay: 1, duration: 0.8 }
            }}
            className="absolute -top-6 -right-4 md:-right-8 bg-white/90 backdrop-blur-xl p-5 rounded-[28px] shadow-[0_20px_40px_rgba(0,0,0,0.08)] border border-white/50 flex items-center gap-4"
          >
            <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center text-xl">⚡</div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("hero.efficiency_label")}</p>
              <p className="text-xl font-black text-slate-900">{t("hero.efficiency_value")}</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeHero;